import { useEffect, useState } from 'react';
import { PruebasChip } from './PruebasChip';
/**
 * Selector de periodo. Lo comparten TODAS las pantallas con números para que "últimos 30 días" signifique lo
 * mismo en todas — si cada pantalla tuviera el suyo, tarde o temprano dirían números distintos del mismo mes y
 * nadie sabría cuál creer.
 *
 * El valor es un texto: '' (todo), un preset ('hoy', '7d', '30d', 'mes', 'mes_pasado', '90d', 'anio') o un
 * rango propio 'r:AAAA-MM-DD:AAAA-MM-DD' ("Fechas…": de cuándo a cuándo). `periodoQS` lo vuelve la parte de
 * la URL que entiende el API (`preset=` o `from=&to=`, lo lee `parsePeriod`).
 *
 * Con qué periodo ABRE cada pantalla lo decide la clínica UNA vez (Administración → Preferencias, 30 días por
 * default): cada pantalla arranca con `useState(usePeriodoInicial())` (lib/preferencias.ts). Antes cada una traía
 * el suyo ("Todo", "Este mes", 90 días…) y dos pantallas abiertas hablaban de periodos distintos sin decirlo.
 */
export const PRESETS: { key: string; label: string }[] = [
  { key: '', label: 'Todo' },
  { key: 'hoy', label: 'Hoy' },
  { key: '7d', label: '7 días' },
  { key: '30d', label: '30 días' },
  { key: 'mes', label: 'Este mes' },
  { key: 'mes_pasado', label: 'Mes pasado' },
  { key: '90d', label: '90 días' },
  { key: 'anio', label: 'Este año' },
];

const RANGO = /^r:(\d{4}-\d{2}-\d{2}):(\d{4}-\d{2}-\d{2})$/;
/** Un rango propio → { from, to } (el orden no importa); cualquier otra cosa → null. */
export function rangoDe(v: string): { from: string; to: string } | null {
  const m = RANGO.exec(v || '');
  if (!m) return null;
  return m[1] <= m[2] ? { from: m[1], to: m[2] } : { from: m[2], to: m[1] };
}
/**
 * La parte de la URL para el API: '' → `vacio` ('' = sin parámetro; algunas pantallas mandan 'todo' porque su
 * API tiene otro default), preset → `preset=…`, rango → `from=…&to=…`.
 */
export function periodoQS(v: string, vacio = ''): string {
  const r = rangoDe(v);
  if (r) return `from=${r.from}&to=${r.to}`;
  if (v) return `preset=${encodeURIComponent(v)}`;
  return vacio ? `preset=${encodeURIComponent(vacio)}` : '';
}
/** Agrega el periodo a unos URLSearchParams (para las pantallas que ya arman los suyos). */
export function ponerPeriodo(qs: URLSearchParams, v: string, vacio = ''): URLSearchParams {
  for (const [k, x] of new URLSearchParams(periodoQS(v, vacio))) qs.set(k, x);
  return qs;
}
const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const corta = (iso: string, conAnio: boolean) => { const [a, m, d] = iso.split('-').map(Number); return `${d} ${MESES[m - 1]}${conAnio ? ` ${a}` : ''}`; };
/** "Del 1 sep al 28 sep 2026" (o el nombre del preset). */
export function periodoTexto(v: string): string {
  const r = rangoDe(v);
  if (r) {
    if (r.from === r.to) return corta(r.from, true);
    const mismoAnio = r.from.slice(0, 4) === r.to.slice(0, 4);
    return `Del ${corta(r.from, !mismoAnio)} al ${corta(r.to, true)}`;
  }
  return PRESETS.find((p) => p.key === v)?.label || 'Todo';
}
/** La fecha de hoy del teléfono o la compu, 'AAAA-MM-DD' (la clínica y quien la usa están en la misma zona). */
const hoyLocal = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const primeroDeMes = () => hoyLocal().slice(0, 8) + '01';

const CSS = `
.pp { display: grid; gap: 8px; min-width: 0; }
.pp-fila { display: flex; gap: 6px; align-items: center; min-width: 0; }
.pp-fila > button { padding: 5px 11px; font-size: 13px; flex: 0 0 auto; white-space: nowrap; box-shadow: none; }
.pp-fila > button[aria-pressed="true"] { border-color: var(--gold); background: var(--gold-light); color: var(--gold-dark); font-weight: 600; }
/* En celular los chips van en UN renglón que se desliza (como las pestañas); en pantalla ancha se acomodan. */
@media (max-width: 699px) {
  .pp-fila { overflow-x: auto; scrollbar-width: none; -webkit-overflow-scrolling: touch; padding-bottom: 2px; margin-right: -16px; padding-right: 16px; }
  .pp-fila::-webkit-scrollbar { display: none; }
}
@media (min-width: 700px) { .pp-fila { flex-wrap: wrap; } }
.pp-rango { display: flex; flex-wrap: wrap; gap: 8px; align-items: flex-end; padding: 10px 12px; border: 1px solid var(--border); border-radius: 14px; background: #fff; }
.pp-rango label { display: grid; gap: 3px; font-size: 11px; font-weight: 700; color: var(--ink-soft); text-transform: uppercase; letter-spacing: .04em; }
.pp-rango input[type=date] { width: auto; min-width: 140px; padding: 7px 10px; font-size: 14px; border-radius: 10px; text-transform: none; }
.pp-rango button { padding: 8px 16px; font-size: 13px; }
`;

/** `sinPruebas`: sin el chip de cuentas de prueba (una pantalla que no cuenta métricas, p. ej. Administración → Accesos). */
export function PeriodPicker({ value, onChange, sinPruebas }: { value: string; onChange: (v: string) => void; sinPruebas?: boolean }) {
  const r = rangoDe(value);
  const [abierto, setAbierto] = useState(false);
  const [desde, setDesde] = useState(r?.from || primeroDeMes());
  const [hasta, setHasta] = useState(r?.to || hoyLocal());
  useEffect(() => { const x = rangoDe(value); if (x) { setDesde(x.from); setHasta(x.to); } }, [value]);
  const aplicar = () => {
    if (!desde || !hasta) return;
    const [f, t] = desde <= hasta ? [desde, hasta] : [hasta, desde];
    onChange(`r:${f}:${t}`); setAbierto(false);
  };
  return (
    <div className="pp" data-periodo={value || 'todo'}>
      <style>{CSS}</style>
      <div className="pp-fila" role="group" aria-label="Periodo">
        {PRESETS.map((p) => (
          <button key={p.key} type="button" className="btn-ghost" aria-pressed={!r && value === p.key}
            data-preset={p.key || 'todo'} onClick={() => { setAbierto(false); onChange(p.key); }}>{p.label}</button>
        ))}
        <button type="button" className="btn-ghost" aria-pressed={!!r} aria-expanded={abierto} data-fechas
          title="Elegir de cuándo a cuándo" onClick={() => setAbierto((x) => !x)}>
          {r ? periodoTexto(value) : 'Fechas…'}
        </button>
        {!sinPruebas && <PruebasChip />}
      </div>
      {abierto && (
        <div className="pp-rango" data-rango>
          <label>Desde<input type="date" value={desde} max={hasta || undefined} onChange={(e) => setDesde(e.target.value)} data-desde /></label>
          <label>Hasta<input type="date" value={hasta} min={desde || undefined} onChange={(e) => setHasta(e.target.value)} data-hasta /></label>
          <button type="button" className="btn-primary" disabled={!desde || !hasta} onClick={aplicar} data-aplicar-rango>Ver estas fechas</button>
          <button type="button" className="btn-ghost" onClick={() => setAbierto(false)}>Cerrar</button>
        </div>
      )}
    </div>
  );
}

/** Aviso de cobertura: por qué un número bajó al elegir un periodo. Sin esto,
 *  "faltan fechas en los datos" es indistinguible de "se vendió menos". */
export function PeriodNote({ children }: { children: React.ReactNode }) {
  if (!children) return null;
  return (
    <div className="card" style={{ padding: '9px 12px', marginBottom: 12, background: 'var(--warning-bg)', color: 'var(--warning)', fontSize: 13, border: 'none' }}>
      {children}
    </div>
  );
}
